import {
  MetastructureFieldType,
  type CustomFieldDefinition,
  type Resource,
  type ResourceRate
} from '__types__';
import type {
  FieldKind,
  FieldSpec
} from 'v5/core/entity-schema/entitySchema.types';
import {
  coreFieldKey,
  coreFieldSpec,
  customFieldSpec,
  type SpecOptions
} from 'v5/core/entity-schema/resource/fieldSpecs';
import { displayCurrency, shortenInterval } from 'v1/helpers/currencyHelper';
import {
  buildShortlistQrValue,
  type ShortlistQrFieldState,
  type ShortlistQrSource
} from 'v5/features/shortlists/display/shortlistQr.logic';
import type { ResourceSchemaCopy } from 'v5/core/entity-schema/resource/resourceSchemaCopy';
import { orderedRates } from 'v5/core/entity-values/codecs/rate.codec';
import { resourceCodecs } from 'v5/core/entity-values/codecs/registry';
import type {
  AnyFormValue,
  CodecContext,
  FieldCodec,
  RowTarget
} from 'v5/core/entity-values/values.types';
import { valueScopeUtils } from 'v5/core/form-schema/formSchema.types';
import {
  currencyDefinitionIds,
  resourceToBuilder
} from 'v5/features/resources/form/resourceBuilderUnits';
import type {
  ResolvedMetastructureField,
  ResolvedMetastructureFieldCore
} from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';

/**
 * A rate as a guest sees it: the amount, as stored (hundredths), and what it
 * is billed by. No name, fees or note.
 */
export type ShortlistRateMoney = {
  currency: string;
  amount: number;
  interval: string | null;
};

/** "£8.50 / day": the legacy view's text, symbol and all. */
export function formatShortlistRate(money: ShortlistRateMoney): string {
  const unit =
    money.interval === null
      ? ''
      : ` / ${shortenInterval(money.interval).toLowerCase()}`;
  return `${displayCurrency(money.amount, money.currency)}${unit}`;
}

/**
 * One known entry of the shortlist's `default_fields`. Every talent of a
 * shortlist gets the same entries in the same order; only what is in them
 * differs, and a field without a value is `empty`.
 */
export type ShortlistDisplayField =
  /** Rendered by the resource kit in display mode from `spec` and `value`. */
  | {
      type: 'kit';
      key: string;
      label: string;
      spec: FieldSpec;
      value: AnyFormValue;
    }
  | { type: 'rate'; key: string; label: string; money: ShortlistRateMoney }
  | { type: 'static'; key: string; label: string; text: string }
  /**
   * A field with no value; `wide` is what its kind needs when it has one, `kind` the kit's kind
   * it reads with one (none for a rate or a name).
   */
  | {
      type: 'empty';
      key: string;
      label: string;
      wide: boolean;
      kind: FieldKind | null;
    }
  /** The QR code, drawn by its own view. */
  | {
      type: 'qr';
      key: string;
      state: ShortlistQrFieldState;
      /** The team's QR code setting for an editor who may open it. */
      setupHref: string | null;
    }
  /** No kit field renders it (QR code, NetSuite master project): the host decides, empty or not. */
  | { type: 'legacy'; key: string };

/** Kinds whose value does not fit half of a narrow sidebar. */
export function isWideSpec(spec: FieldSpec): boolean {
  switch (spec.kind) {
    case 'tags':
    case 'groups':
    case 'multi-select':
    case 'contact-details':
    case 'date-range':
    case 'dimension':
      return true;
    case 'text':
      return spec.multiline;
    default:
      return false;
  }
}

/** Which fields the talent's resource type shows. */
export type ShortlistTypeFields =
  /** A guest's payload names no type: every field of `default_fields` shows. */
  | { type: 'unknown' }
  /**
   * The type's active fields, as the resource pane resolves them (the
   * browsing workspace's overlay included): core field names and custom
   * field definition ids.
   */
  | {
      type: 'known';
      coreNames: ReadonlySet<string>;
      customFieldIds: ReadonlySet<number>;
      /** The custom fields among them that the type requires. */
      requiredCustomFieldIds: ReadonlySet<number>;
    };

/** The fields a resource type shows, from its metastructure in the browsing scope. */
export function shortlistTypeFieldsOf(
  metastructure: ReadonlyArray<ResolvedMetastructureField>
): ShortlistTypeFields {
  const active = metastructure.filter(field => field.active);
  return {
    type: 'known',
    coreNames: new Set(
      active.flatMap(field =>
        field.type === MetastructureFieldType.Core ? [field.name] : []
      )
    ),
    customFieldIds: new Set(
      active.flatMap(field =>
        field.type === MetastructureFieldType.Custom
          ? [field.customFieldDefinition.id]
          : []
      )
    ),
    requiredCustomFieldIds: new Set(
      active.flatMap(field =>
        field.type === MetastructureFieldType.Custom && field.required
          ? [field.customFieldDefinition.id]
          : []
      )
    )
  };
}

export type ShortlistDisplayInput = {
  /** `shortlist.default_fields`: keys in display order. */
  defaultFields: ReadonlyArray<string>;
  /** As the entities hold it (amounts in hundredths), or a guest's picked contact. */
  resource: Resource;
  definitions: ReadonlyArray<CustomFieldDefinition>;
  /** Browsing lens for custom fields; `null` = global values. */
  workspaceId: number | null;
  /** The translated label of a field key, else `fallback`. */
  labelOf(key: string, fallback: string): string;
  copy: ResourceSchemaCopy;
  typeFields: ShortlistTypeFields;
  qr: ShortlistQrSource;
};

const SOCIAL_PLATFORMS = [
  'instagram',
  'twitter',
  'behance',
  'youtube',
  'vimeo'
] as const;
type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

const CORE_FIELD_KEYS = [
  'websites',
  'emails',
  'phone_numbers',
  'locations',
  'tags',
  'groups'
] as const;
type CoreFieldKey = (typeof CORE_FIELD_KEYS)[number];

function socialPlatformOf(key: string): SocialPlatform | null {
  return (
    SOCIAL_PLATFORMS.find(platform => `social_${platform}` === key) ?? null
  );
}

function isCoreFieldKey(key: string): key is CoreFieldKey {
  return (CORE_FIELD_KEYS as ReadonlyArray<string>).includes(key);
}

/** The label of a key without a `core_fields.<key>` translation. */
export const FALLBACK_LABEL: Record<string, string> = {
  social_instagram: 'Instagram',
  social_twitter: 'Twitter',
  social_behance: 'Behance',
  social_youtube: 'Youtube',
  social_vimeo: 'Vimeo',
  websites: 'Website',
  emails: 'Email',
  phone_numbers: 'Phone number',
  locations: 'Location',
  rates: 'Rate',
  tags: 'Tags',
  groups: 'Groups',
  organisation: 'Organization',
  represented_by: 'Agent'
};

const FIRST = 0;

type Context = {
  input: ShortlistDisplayInput;
  /** The resource in the units the codecs read (amounts, not hundredths). */
  resource: Resource;
  options: SpecOptions;
  scope: CodecContext['scope'];
};

/**
 * Tags and groups carry their words in the spec's options: the codecs
 * decode them to ids. Everything else of `SpecOptions` serves editing.
 */
function specOptions(
  resource: Resource,
  copy: ResourceSchemaCopy
): SpecOptions {
  return {
    resourceTypeModel: 'INDIVIDUAL',
    resourceTypeId: resource.resource_type_id ?? 0,
    tags: (resource.tags ?? []).map(tag => ({
      value: String(tag.id),
      label: tag.tag
    })),
    allowCreateTags: false,
    groups: (resource.groups ?? []).map(group => ({
      value: String(group.id),
      label: group.name ?? String(group.id),
      colour: group.colour ?? null
    })),
    pronouns: [],
    currencies: [],
    defaultCurrency: '',
    rateCards: [],
    rateCardsSettingsPath: null,
    copy
  };
}

function coreField(name: string): ResolvedMetastructureFieldCore {
  return {
    type: MetastructureFieldType.Core,
    name,
    active: true,
    locked: true,
    required: false,
    default: ''
  };
}

/**
 * The value of `spec` in `resource`. Lists show their first row (a contact's
 * first email, as the legacy view did).
 */
function decoded(
  spec: FieldSpec,
  resource: Resource,
  ctx: Context,
  target: RowTarget = { type: 'entity' }
): { value: AnyFormValue; isEmpty: boolean } {
  const codec: FieldCodec<FieldKind> = resourceCodecs[spec.kind];
  const value = codec.decode(resource, spec, { scope: ctx.scope, target });
  const first =
    spec.kind === 'contact-details' && Array.isArray(value)
      ? value.slice(FIRST, FIRST + 1)
      : value;
  return { value: first, isEmpty: codec.isEmpty(first, spec) };
}

function kitField(
  key: string,
  spec: FieldSpec,
  value: { value: AnyFormValue; isEmpty: boolean }
): ShortlistDisplayField {
  return value.isEmpty
    ? {
        type: 'empty',
        key,
        label: spec.label,
        wide: isWideSpec(spec),
        kind: spec.kind
      }
    : { type: 'kit', key, label: spec.label, spec, value: value.value };
}

function customField(key: string, ctx: Context): ShortlistDisplayField | null {
  const id = Number(key.slice('custom_'.length));
  const definition = ctx.input.definitions.find(
    candidate => candidate.id === id
  );
  if (definition === undefined) return null;
  const spec = {
    ...customFieldSpec(definition, false, true, ctx.input.copy),
    label: ctx.input.labelOf(key, definition.name)
  };
  return spec.kind === 'netsuite-master-project'
    ? { type: 'legacy', key }
    : kitField(key, spec, decoded(spec, ctx.resource, ctx));
}

/**
 * A resource core field under the shortlist's key (`socials` is one field
 * per platform). `row: false` is a list that has no row to show.
 */
function coreKitField(
  key: string,
  column: CoreFieldKey | 'socials',
  resource: Resource,
  ctx: Context,
  row: { target: RowTarget; present: boolean } = {
    target: { type: 'entity' },
    present: true
  }
): ShortlistDisplayField | null {
  const base = coreFieldSpec(coreField(column), ctx.options);
  if (base === null) return null;
  const spec = {
    ...base,
    key: coreFieldKey(key),
    label: ctx.input.labelOf(key, FALLBACK_LABEL[key])
  };
  return row.present
    ? kitField(key, spec, decoded(spec, resource, ctx, row.target))
    : {
        type: 'empty',
        key,
        label: spec.label,
        wide: isWideSpec(spec),
        kind: spec.kind
      };
}

/** The amount, currency and unit a rate row bills; a card row reads the card it embeds. */
function billedBy(row: ResourceRate) {
  const source =
    row.rate_id === null || row.rate_id === undefined ? row : row.rate;
  return source
    ? {
        currency: source.currency ?? '',
        amount: source.amount ?? null,
        interval: source.interval ?? null
      }
    : null;
}

/**
 * The first rate, as money. Nothing but amount, currency and billing unit
 * leaves this function, so name, fees and note cannot reach a guest through
 * the view. Read from the resource as the entities hold it: the legacy
 * formatting wants hundredths.
 */
function rateField(key: string, ctx: Context): ShortlistDisplayField {
  const row = orderedRates(ctx.input.resource)[FIRST];
  const billed = row === undefined ? null : billedBy(row);
  const label = ctx.input.labelOf(key, FALLBACK_LABEL[key]);
  if (billed === null || billed.amount === null) {
    return { type: 'empty', key, label, wide: false, kind: null };
  }
  return {
    type: 'rate',
    key,
    label,
    money: {
      currency: billed.currency,
      amount: billed.amount,
      interval: billed.interval
    }
  };
}

function staticField(
  key: string,
  name: string | undefined,
  ctx: Context
): ShortlistDisplayField {
  const text = name?.trim() ?? '';
  const label = ctx.input.labelOf(key, FALLBACK_LABEL[key]);
  return text === ''
    ? { type: 'empty', key, label, wide: false, kind: null }
    : { type: 'static', key, label, text };
}

function qrField(key: string, ctx: Context): ShortlistDisplayField | null {
  const { qr, resource, definitions, workspaceId, typeFields } = ctx.input;
  switch (qr.type) {
    case 'off':
      return { type: 'legacy', key };
    case 'loading':
      return { type: 'qr', key, state: { type: 'loading' }, setupHref: null };
    case 'on': {
      const { audience } = qr;
      const setupHref = audience.type === 'editor' ? audience.setupHref : null;
      if (qr.setting === null) {
        return setupHref === null
          ? null
          : { type: 'qr', key, state: { type: 'not-set-up' }, setupHref };
      }
      const value = buildShortlistQrValue({
        contact: resource,
        definitions,
        config: qr.setting,
        workspaceId,
        typeFields
      });
      if (audience.type === 'viewer') {
        return value.type === 'cannot-build'
          ? null
          : {
              type: 'qr',
              key,
              state: { ...value, missingSlots: [] },
              setupHref
            };
      }
      return { type: 'qr', key, state: value, setupHref };
    }
  }
}

function fieldOf(key: string, ctx: Context): ShortlistDisplayField | null {
  if (key === 'qr_code') return qrField(key, ctx);
  if (key === 'rates') return rateField(key, ctx);
  if (key === 'organisation') {
    return staticField(key, ctx.resource.organisation?.full_name, ctx);
  }
  if (key === 'represented_by') {
    return staticField(key, ctx.resource.represented_by?.full_name, ctx);
  }
  if (key.startsWith('custom_')) return customField(key, ctx);
  const platform = socialPlatformOf(key);
  if (platform !== null) {
    const ofPlatform = {
      ...ctx.resource,
      socials: (ctx.resource.socials ?? []).filter(
        social => social.value_2 === platform
      )
    };
    return coreKitField(key, 'socials', ofPlatform, ctx);
  }
  if (key === 'locations') {
    // The codec throws on a missing row; a guest gets `null` for none.
    return coreKitField(key, key, ctx.resource, ctx, {
      target: { type: 'locations-item', index: FIRST },
      present: (ctx.resource.locations ?? []).length > 0
    });
  }
  return isCoreFieldKey(key) ? coreKitField(key, key, ctx.resource, ctx) : null;
}

/** The metastructure field a shortlist key reads. */
function coreNameOf(key: string): string {
  if (socialPlatformOf(key) !== null) return 'socials';
  if (key === 'organisation' || key === 'represented_by') return `${key}_id`;
  return key;
}

/** Whether the talent's type shows the field a shortlist key reads. */
export function shownByType(
  key: string,
  typeFields: ShortlistTypeFields
): boolean {
  if (typeFields.type === 'unknown' || key === 'qr_code') return true;
  if (key.startsWith('custom_')) {
    return typeFields.customFieldIds.has(Number(key.slice('custom_'.length)));
  }
  return typeFields.coreNames.has(coreNameOf(key));
}

/**
 * What the shortlist's sidebar shows of a resource: one entry per known key
 * of `default_fields`, in its order, whatever the resource holds, so every
 * talent of a type reads the same form. A field the talent's type doesn't
 * show is left out, as the resource pane leaves it out; so are unknown and
 * repeated keys.
 */
export function buildShortlistDisplayFields(
  input: ShortlistDisplayInput
): ShortlistDisplayField[] {
  const resource = resourceToBuilder(
    input.resource,
    currencyDefinitionIds(
      Object.fromEntries(
        input.definitions.map(definition => [definition.id, definition])
      )
    )
  );
  const ctx: Context = {
    input,
    resource,
    options: specOptions(resource, input.copy),
    scope: valueScopeUtils.fromWorkspaceId(input.workspaceId)
  };
  const fields: ShortlistDisplayField[] = [];
  for (const key of new Set(input.defaultFields)) {
    if (!shownByType(key, input.typeFields)) continue;
    const field = fieldOf(key, ctx);
    if (field !== null) fields.push(field);
  }
  return fields;
}
